// Employment episode source systems (ADR-049 / ADR-136)
//
// An employment episode can carry a `payload.sourceReference` = { source, contractId }
// linking it to the contract it represents in the system of record. Ingestion
// stamps its own identifier ('americas-radar', 'powerbi', 'sharepoint'); contracts
// entered in this app are stamped 'radar'.

/**
 * Source-system identifiers. Only the value this app stamps lives here — the
 * ingestion identifiers are owned by radar-ingestion and are never written from
 * the UI (an operator backfilling one types it by hand).
 */
export const SOURCE_SYSTEMS = {
  RADAR: 'radar',
};

/**
 * ADR-136: the source stamped on contracts captured in this app. Applied at the
 * ENTRY SURFACES only (hire form, episode annotation) — never server-side, so a
 * caller that deliberately omits `source` keeps that choice (ADR-049 treats the
 * episode payload as the historical original).
 */
export const DEFAULT_SOURCE_SYSTEM = SOURCE_SYSTEMS.RADAR;

/**
 * The name a PERSON knows each source by. A stamp is an ingestion identifier
 * ('powerbi' is the Enova feed, delivered through PowerBI) and is never shown
 * as-is. Mirrors radar-api's SOURCE_SYSTEM_LABELS (utils/episodeSourceOwnership).
 */
export const SOURCE_SYSTEM_LABELS = Object.freeze({
  powerbi: 'Enova',
  'americas-radar': 'Americas',
  sharepoint: 'SharePoint',
  [SOURCE_SYSTEMS.RADAR]: 'Radar'
});

/** A source stamp as users name it; an unknown stamp is shown verbatim. */
export const sourceSystemLabel = (source) => (source ? (SOURCE_SYSTEM_LABELS[source] || source) : null);

/**
 * ADR-136 A5: an episode is "ours" when it carries no source stamp (it predates
 * the stamp, or was created here) or is explicitly stamped 'radar'. Episodes
 * owned by an ingestion source are excluded — their comments are maintained by
 * that sync (ADR-031) and would be overwritten.
 * @param {{source?: string}} [sourceRef] - payload.sourceReference
 */
export const isRadarSourced = (sourceRef) =>
  !sourceRef?.source || sourceRef.source === SOURCE_SYSTEMS.RADAR;

/**
 * ADR-049/137: normalize a source-contract-id input to the value the API stores.
 *
 * Default (2026-08-05 fix): the trimmed input VERBATIM, as free text. A contract
 * number is an identifier, not a quantity — "0001021" and "1021" are different
 * identifiers in a system of record, so radar-entered numbers must keep their
 * leading zeros. (Before this fix, purely numeric input was always coerced to
 * Number, which silently stripped them.)
 *
 * `numericAsNumber: true` restores the numeric coercion it was once built for
 * (hand-backfilling an ingestion-owned id). ADR-174 §5.5 retired its last
 * caller: a number a PERSON enters is text, the API refuses a JSON number from
 * a person, and the Americas sync matches ids as String() === String(), so a
 * text id matches its numeric source anyway. Kept only for compatibility.
 *
 * Empty -> null (nothing to link).
 */
export const toSourceContractId = (val, { numericAsNumber = false } = {}) => {
  const trimmed = (val ?? '').toString().trim();
  if (trimmed === '') return null;
  return numericAsNumber && /^\d+$/.test(trimmed) ? Number(trimmed) : trimmed;
};

/**
 * ADR-183 — a sync-owned contract is read-only to people in radar: only an
 * admin may change it (the escape hatch for bad source data). The server
 * enforces this (radar-api syncOwnedContractGuard); these helpers decide what
 * the UI offers, so an operator never meets the refusal by surprise.
 */
export const isLockedBySource = (sourceRef, viewerIsAdmin) => !viewerIsAdmin && !isRadarSourced(sourceRef);

/**
 * The person's first open sync-owned contract — what locks the PERSON verbs
 * (Resign, Hire Withdraw, Mark No Show), which end every contract they hold
 * (ADR-183 D2). Reads `contracts[]` (ADR-156) when the payload carries it,
 * else the primary's flat `currentEpisodeSource`.
 * @returns {{ source: string } | null}
 */
export const syncOwnedOpenContract = (employee) => {
  if (Array.isArray(employee?.contracts)) {
    return employee.contracts.find((c) => !c.endDate && !isRadarSourced({ source: c.source })) || null;
  }
  const source = employee?.currentEpisodeSource;
  return isRadarSourced({ source }) ? null : { source };
};

/** ADR-183 D2 — null when the viewer may run person verbs, else the locking contract. */
export const personVerbsLockedBy = (employee, viewerIsAdmin) => (viewerIsAdmin ? null : syncOwnedOpenContract(employee));

/** The note shown where a locked action would have been. */
export const sourceLockNote = (source, { person = false } = {}) => {
  const system = sourceSystemLabel(source) || 'its source system';
  return person
    ? `This person holds a contract maintained by ${system}: Resign, Hire Withdraw and Mark No Show are recorded in ${system}.`
    : `Maintained by ${system}: changes to this contract are made in ${system}.`;
};

